import { useAPIQuery } from "@dev2_techwalnut/tw-api-query-hook";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import secureLocalStorage from "react-secure-storage";

const tabs = [
    {
        key: "overview",
        label: "Overview",
        icon: "ni ni-dashboard",
        privilege: "bridge_console_notifications",
    },
    {
        key: "delivery-logs",
        label: "Delivery Logs",
        icon: "ni ni-send",
        privilege: "bridge_console_notifications",
    },
    {
        key: "dispatch-events",
        label: "Dispatch Events",
        icon: "ni ni-activity-round",
        privilege: "bridge_console_notifications",
    },
    {
        key: "templates",
        label: "Templates & Channels",
        icon: "ni ni-setting",
        privilege: "bridge_console_notification_config",
    },
    {
        key: "recipients",
        label: "Recipients",
        icon: "ni ni-users",
        privilege: "bridge_console_notification_config",
    },
];

const getPayload = (response, fallback) =>
    response?.results?.data ?? response?.data?.results?.data ?? fallback;

const getCount = (response, fallback = 0) =>
    response?.count ?? response?.data?.count ?? fallback;

const getNumPages = (response) =>
    response?.num_pages ?? response?.data?.num_pages ?? 1;

const formatDate = (value) => {
    if (!value) return "-";
    return moment(value).format("DD MMM YYYY, hh:mm A");
};

const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    return value;
};

const Spinner = () => (
    <div className="spinner-border text-primary ms-2" role="status">
        <span className="sr-only"></span>
    </div>
);

const StatCard = ({ label, value, icon }) => (
    <div className="col-sm-6 col-xl-3">
        <div className="card card-bordered">
            <div className="card-inner">
                <div className="d-flex align-items-center">
                    <div className="user-avatar bg-primary-dim">
                        <em className={`icon ${icon}`}></em>
                    </div>
                    <div className="ms-3">
                        <span className="sub-text">{label}</span>
                        <h5 className="mb-0">{value}</h5>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

const PaginationControls = ({ page, pageCount, onPageChange }) => (
    <div className="d-flex align-items-center justify-content-end gap-2 pt-3">
        <button
            type="button"
            className="btn btn-sm btn-outline-light"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
        >
            <em className="icon ni ni-chevron-left"></em>
        </button>
        <span className="sub-text">
            Page {page} of {pageCount || 1}
        </span>
        <button
            type="button"
            className="btn btn-sm btn-outline-light"
            disabled={page >= pageCount}
            onClick={() => onPageChange(page + 1)}
        >
            <em className="icon ni ni-chevron-right"></em>
        </button>
    </div>
);

const DeliveryBadge = ({ summary }) => {
    if (!summary) return "-";
    if (summary.failed > 0) {
        return (
            <span className="badge badge-dim bg-danger">
                Failed {summary.failed}/{summary.total}
            </span>
        );
    }
    if (summary.pending > 0) {
        return (
            <span className="badge badge-dim bg-warning">
                Pending {summary.pending}/{summary.total}
            </span>
        );
    }
    return (
        <span className="badge badge-dim bg-success">
            Delivered {summary.delivered}/{summary.total}
        </span>
    );
};

const ScopeBadge = ({ scope }) => {
    if (scope === "department") {
        return (
            <span className="badge badge-dim bg-info text-dark">
                Department
            </span>
        );
    }
    return (
        <span className="badge badge-dim bg-light text-dark">All users</span>
    );
};

const NotificationConsole = () => {
    const userPrivileges = secureLocalStorage.getItem("privileges") || [];
    const visibleTabs = useMemo(
        () => tabs.filter((tab) => userPrivileges.includes(tab.privilege)),
        [userPrivileges]
    );
    const canViewLogs = userPrivileges.includes("bridge_console_notifications");
    const canManageConfig = userPrivileges.includes(
        "bridge_console_notification_config"
    );

    const [activeTab, setActiveTab] = useState(visibleTabs?.[0]?.key ?? "");
    const [entries, setEntries] = useState(20);
    const [deliveryPage, setDeliveryPage] = useState(1);
    const [dispatchPage, setDispatchPage] = useState(1);
    const [templatePage, setTemplatePage] = useState(1);
    const [deliverySearch, setDeliverySearch] = useState("");
    const [deliveryStatus, setDeliveryStatus] = useState("");
    const [dispatchStatus, setDispatchStatus] = useState("");
    const [dispatchSearch, setDispatchSearch] = useState("");
    const [templateSearch, setTemplateSearch] = useState("");
    const [recipientSearch, setRecipientSearch] = useState("");
    const [selectedLog, setSelectedLog] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedRecipientTemplate, setSelectedRecipientTemplate] =
        useState(null);

    useEffect(() => {
        if (
            visibleTabs.length > 0 &&
            !visibleTabs.some((tab) => tab.key === activeTab)
        ) {
            setActiveTab(visibleTabs[0].key);
        }
    }, [activeTab, visibleTabs]);

    const { useFetch: useFetchOverview } = useAPIQuery({
        key: "notification-console-overview",
        endpoint: "/bridge-console/notifications/overview/",
    });
    const { useFetch: useFetchDeliveryLogs } = useAPIQuery({
        key: "notification-console-delivery-logs",
        endpoint: "/bridge-console/notifications/logs/",
    });
    const { useFetch: useFetchDispatchLogs } = useAPIQuery({
        key: "notification-console-dispatch-logs",
        endpoint: "/bridge-console/notifications/dispatch-logs/",
    });
    const { useFetch: useFetchTemplates } = useAPIQuery({
        key: "notification-console-templates",
        endpoint: "/bridge-console/notifications/templates/",
    });
    const { useFetch: useFetchDispensers } = useAPIQuery({
        key: "notification-console-dispensers",
        endpoint: "/bridge-console/notifications/dispensers/",
    });
    const { useFetch: useFetchRecipientRules } = useAPIQuery({
        key: "notification-console-recipient-rules",
        endpoint: "/bridge-console/notifications/recipient-rules/",
    });
    const { useCreate: useSeedTemplates } = useAPIQuery({
        key: "notification-console-seed-templates",
        endpoint: "/bridge-console/notifications/seed-templates/",
    });
    const { useUpdate: useUpdateTemplate } = useAPIQuery({
        key: "notification-console-template-update",
        endpoint: "/bridge-console/notifications/templates/",
    });
    const { useCreate: useResendNotification } = useAPIQuery({
        key: "notification-console-resend",
        endpoint: "/bridge-console/notifications/resend/",
    });

    const overviewQuery = useFetchOverview({
        enabled: canViewLogs && activeTab === "overview",
        staleTime: 1000 * 60 * 2,
    });
    const deliveryLogsQuery = useFetchDeliveryLogs({
        params: {
            entries,
            page_no: deliveryPage,
            q: deliverySearch || undefined,
            delivery_status: deliveryStatus || undefined,
        },
        enabled: canViewLogs && activeTab === "delivery-logs",
        abortOnNewRequest: true,
        staleTime: 1000 * 60,
    });
    const dispatchLogsQuery = useFetchDispatchLogs({
        params: {
            entries,
            page_no: dispatchPage,
            q: dispatchSearch || undefined,
            status: dispatchStatus || undefined,
        },
        enabled: canViewLogs && activeTab === "dispatch-events",
        abortOnNewRequest: true,
        staleTime: 1000 * 60,
    });
    const templatesQuery = useFetchTemplates({
        params: {
            entries,
            page_no: templatePage,
            q: templateSearch || undefined,
        },
        enabled: canManageConfig && activeTab === "templates",
        abortOnNewRequest: true,
        staleTime: 1000 * 60 * 2,
    });
    const dispensersQuery = useFetchDispensers({
        params: { entries: 100, page_no: 1 },
        enabled: canManageConfig && activeTab === "templates",
        staleTime: 1000 * 60 * 5,
    });
    const recipientRulesQuery = useFetchRecipientRules({
        params: {
            q: recipientSearch || undefined,
        },
        enabled: canManageConfig && activeTab === "recipients",
        staleTime: 1000 * 60 * 2,
    });

    const { mutate: seedTemplates, isPending: seedingTemplates } =
        useSeedTemplates({
            onSuccess: (response) => {
                Swal.fire(
                    "Synced",
                    response?.results?.message ||
                        "Process notification templates synced.",
                    "success"
                );
                templatesQuery.refetch();
                recipientRulesQuery.refetch();
                overviewQuery.refetch();
            },
            onError: (error) => {
                Swal.fire(
                    "Sync failed",
                    error?.response?.data?.results?.error ||
                        "Could not sync templates.",
                    "error"
                );
            },
        });

    const { mutate: updateTemplate, isPending: updatingTemplate } =
        useUpdateTemplate({
            onSuccess: (response) => {
                Swal.fire(
                    "Saved",
                    response?.results?.message || "Template updated.",
                    "success"
                );
                templatesQuery.refetch();
                recipientRulesQuery.refetch();
                overviewQuery.refetch();
            },
            onError: (error) => {
                Swal.fire(
                    "Update failed",
                    error?.response?.data?.results?.error ||
                        "Could not update template.",
                    "error"
                );
            },
        });

    const { mutate: resendNotification, isPending: resendingNotification } =
        useResendNotification({
            onSuccess: (response) => {
                Swal.fire(
                    "Resent",
                    response?.results?.message ||
                        "Notification resent successfully.",
                    "success"
                );
                deliveryLogsQuery.refetch();
            },
            onError: (error) => {
                Swal.fire(
                    "Resend failed",
                    error?.response?.data?.results?.error ||
                        "Could not resend notification.",
                    "error"
                );
            },
        });

    const overview = getPayload(overviewQuery.data, {});
    const deliveryLogs = getPayload(deliveryLogsQuery.data, []);
    const dispatchLogs = getPayload(dispatchLogsQuery.data, []);
    const templates = getPayload(templatesQuery.data, []);
    const dispensers = getPayload(dispensersQuery.data, []);
    const recipientRulesPayload = getPayload(recipientRulesQuery.data, {});
    const recipientTemplates = recipientRulesPayload.templates || [];
    const availableRoles = recipientRulesPayload.available_roles || [];
    const scopeOptions = recipientRulesPayload.scope_options || [];

    const handleEntriesChange = (event) => {
        setEntries(Number(event.target.value));
        setDeliveryPage(1);
        setDispatchPage(1);
        setTemplatePage(1);
    };

    const handleSaveTemplate = () => {
        if (!selectedTemplate) return;
        updateTemplate({
            id: selectedTemplate.id,
            data: {
                template_name: selectedTemplate.template_name,
                subject: selectedTemplate.subject,
                header_mobile: selectedTemplate.header_mobile,
                message: selectedTemplate.message,
                is_allowed: selectedTemplate.is_allowed,
                is_notification_enabled:
                    selectedTemplate.is_notification_enabled,
                dispenser_links: (selectedTemplate.dispensers || []).map(
                    (link) => ({
                        link_id: link.link_id,
                        is_allowed: link.is_allowed,
                    })
                ),
            },
        });
    };

    const handleSaveRecipientRules = () => {
        if (!selectedRecipientTemplate) return;
        updateTemplate({
            id: selectedRecipientTemplate.id,
            data: {
                recipient_rules: (
                    selectedRecipientTemplate.recipient_rules || []
                ).map((rule) => ({
                    role_value: rule.role_value,
                    scope: rule.scope,
                    is_enabled: rule.is_enabled !== false,
                })),
            },
        });
    };

    const handleAddRecipientRule = () => {
        const defaultRole = availableRoles[0]?.role_value || "";
        if (!defaultRole) return;
        setSelectedRecipientTemplate((prev) => ({
            ...prev,
            recipient_rules: [
                ...(prev.recipient_rules || []),
                {
                    role_value: defaultRole,
                    scope: "global",
                    is_enabled: true,
                },
            ],
        }));
    };

    const handleRemoveRecipientRule = (index) => {
        setSelectedRecipientTemplate((prev) => ({
            ...prev,
            recipient_rules: (prev.recipient_rules || []).filter(
                (_, ruleIndex) => ruleIndex !== index
            ),
        }));
    };

    const handleResend = (notificationId) => {
        resendNotification({
            data: { notification_id: notificationId },
        });
    };

    const isFetching =
        overviewQuery.isFetching ||
        deliveryLogsQuery.isFetching ||
        dispatchLogsQuery.isFetching ||
        templatesQuery.isFetching ||
        dispensersQuery.isFetching ||
        recipientRulesQuery.isFetching;

    return (
        <>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                    <div className="nk-block-head-content d-flex align-items-center">
                        <h3 className="nk-block-title page-title">
                            Notification Console
                        </h3>
                        {isFetching && <Spinner />}
                    </div>
                    <div className="nk-block-head-content">
                        <div className="form-control-wrap">
                            <select
                                className="form-select form-select-sm"
                                value={entries}
                                onChange={handleEntriesChange}
                            >
                                <option value={10}>10 entries</option>
                                <option value={20}>20 entries</option>
                                <option value={50}>50 entries</option>
                                <option value={100}>100 entries</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {visibleTabs.length === 0 ? (
                <div className="alert alert-light">
                    No notification console privileges are assigned to this user.
                </div>
            ) : (
                <div className="nk-block">
                    <div className="card card-bordered">
                        <div className="card-inner border-bottom">
                            <ul className="nav nav-tabs mt-n3">
                                {visibleTabs.map((tab) => (
                                    <li className="nav-item" key={tab.key}>
                                        <button
                                            type="button"
                                            className={`nav-link ${
                                                activeTab === tab.key
                                                    ? "active"
                                                    : ""
                                            }`}
                                            onClick={() =>
                                                setActiveTab(tab.key)
                                            }
                                        >
                                            <em
                                                className={`icon ${tab.icon} me-1`}
                                            ></em>
                                            {tab.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {activeTab === "overview" && (
                            <div className="card-inner">
                                <div className="row g-gs mb-4">
                                    <StatCard
                                        label="Notifications Sent"
                                        value={overview.notifications_total ?? 0}
                                        icon="ni-send"
                                    />
                                    <StatCard
                                        label="Active Templates"
                                        value={overview.templates_total ?? 0}
                                        icon="ni-file-text"
                                    />
                                    <StatCard
                                        label="Enabled Templates"
                                        value={overview.templates_enabled ?? 0}
                                        icon="ni-check-circle"
                                    />
                                    <StatCard
                                        label="Active Dispensers"
                                        value={overview.dispensers_total ?? 0}
                                        icon="ni-link"
                                    />
                                </div>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <div className="card card-bordered h-100">
                                            <div className="card-inner">
                                                <h6 className="title">
                                                    Delivery Status
                                                </h6>
                                                {Object.entries(
                                                    overview.delivery_status_counts ||
                                                        {}
                                                ).map(([key, value]) => (
                                                    <div
                                                        key={key}
                                                        className="d-flex justify-content-between py-1"
                                                    >
                                                        <span>{key}</span>
                                                        <strong>{value}</strong>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="card card-bordered h-100">
                                            <div className="card-inner">
                                                <h6 className="title">
                                                    Dispatch Events
                                                </h6>
                                                {Object.entries(
                                                    overview.dispatch_status_counts ||
                                                        {}
                                                ).map(([key, value]) => (
                                                    <div
                                                        key={key}
                                                        className="d-flex justify-content-between py-1"
                                                    >
                                                        <span>{key}</span>
                                                        <strong>{value}</strong>
                                                    </div>
                                                ))}
                                                <div className="sub-text mt-2">
                                                    Process events defined:{" "}
                                                    {overview.process_events_defined ??
                                                        0}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === "delivery-logs" && (
                            <div className="card-inner">
                                <div className="row g-3 mb-3">
                                    <div className="col-md-4">
                                        <input
                                            className="form-control"
                                            placeholder="Search message, template, recipient"
                                            value={deliverySearch}
                                            onChange={(event) => {
                                                setDeliverySearch(
                                                    event.target.value
                                                );
                                                setDeliveryPage(1);
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <select
                                            className="form-select"
                                            value={deliveryStatus}
                                            onChange={(event) => {
                                                setDeliveryStatus(
                                                    event.target.value
                                                );
                                                setDeliveryPage(1);
                                            }}
                                        >
                                            <option value="">
                                                All delivery statuses
                                            </option>
                                            <option value="Delivered">
                                                Delivered
                                            </option>
                                            <option value="Failed">
                                                Failed
                                            </option>
                                            <option value="Pending">
                                                Pending
                                            </option>
                                        </select>
                                    </div>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-tranx">
                                        <thead>
                                            <tr className="tb-tnx-head">
                                                <th>Date</th>
                                                <th>Event</th>
                                                <th>Recipient</th>
                                                <th>Subject</th>
                                                <th>Delivery</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {deliveryLogs.map((log) => (
                                                <tr key={log.id}>
                                                    <td>
                                                        {formatDate(
                                                            log.occurred_at
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div>
                                                            {formatValue(
                                                                log.template_name
                                                            )}
                                                        </div>
                                                        <div className="sub-text">
                                                            {formatValue(
                                                                log.event_key
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {formatValue(
                                                            log.recipient
                                                                ?.username
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatValue(
                                                            log.final_subject
                                                        )}
                                                    </td>
                                                    <td>
                                                        <DeliveryBadge
                                                            summary={
                                                                log.delivery_summary
                                                            }
                                                        />
                                                    </td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary me-1"
                                                            data-bs-toggle="modal"
                                                            data-bs-target="#notification-log-detail-modal"
                                                            onClick={() =>
                                                                setSelectedLog(
                                                                    log
                                                                )
                                                            }
                                                        >
                                                            Details
                                                        </button>
                                                        {canManageConfig && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-warning"
                                                                disabled={
                                                                    resendingNotification
                                                                }
                                                                onClick={() =>
                                                                    handleResend(
                                                                        log.id
                                                                    )
                                                                }
                                                            >
                                                                Resend
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <PaginationControls
                                    page={deliveryPage}
                                    pageCount={getNumPages(
                                        deliveryLogsQuery.data
                                    )}
                                    onPageChange={setDeliveryPage}
                                />
                            </div>
                        )}

                        {activeTab === "dispatch-events" && (
                            <div className="card-inner">
                                <div className="row g-3 mb-3">
                                    <div className="col-md-4">
                                        <input
                                            className="form-control"
                                            placeholder="Search event or error"
                                            value={dispatchSearch}
                                            onChange={(event) => {
                                                setDispatchSearch(
                                                    event.target.value
                                                );
                                                setDispatchPage(1);
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <select
                                            className="form-select"
                                            value={dispatchStatus}
                                            onChange={(event) => {
                                                setDispatchStatus(
                                                    event.target.value
                                                );
                                                setDispatchPage(1);
                                            }}
                                        >
                                            <option value="">
                                                All dispatch statuses
                                            </option>
                                            <option value="dispatched">
                                                Dispatched
                                            </option>
                                            <option value="skipped">
                                                Skipped
                                            </option>
                                            <option value="failed">
                                                Failed
                                            </option>
                                        </select>
                                    </div>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-tranx">
                                        <thead>
                                            <tr className="tb-tnx-head">
                                                <th>Date</th>
                                                <th>Event</th>
                                                <th>Status</th>
                                                <th>Recipients</th>
                                                <th>Error</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dispatchLogs.map((log) => (
                                                <tr key={log.id}>
                                                    <td>
                                                        {formatDate(
                                                            log.occurred_at
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div>
                                                            {formatValue(
                                                                log.template
                                                                    ?.template_name
                                                            )}
                                                        </div>
                                                        <div className="sub-text">
                                                            {formatValue(
                                                                log.event_key
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span
                                                            className={`badge badge-dim ${
                                                                log.status ===
                                                                "dispatched"
                                                                    ? "bg-success"
                                                                    : log.status ===
                                                                      "skipped"
                                                                    ? "bg-warning"
                                                                    : "bg-danger"
                                                            }`}
                                                        >
                                                            {log.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {formatValue(
                                                            log.recipient_count
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatValue(
                                                            log.error_message
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <PaginationControls
                                    page={dispatchPage}
                                    pageCount={getNumPages(
                                        dispatchLogsQuery.data
                                    )}
                                    onPageChange={setDispatchPage}
                                />
                            </div>
                        )}

                        {activeTab === "templates" && (
                            <div className="card-inner">
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                                    <div className="row g-3 flex-grow-1 mb-0">
                                        <div className="col-md-4">
                                            <input
                                                className="form-control"
                                                placeholder="Search template"
                                                value={templateSearch}
                                                onChange={(event) => {
                                                    setTemplateSearch(
                                                        event.target.value
                                                    );
                                                    setTemplatePage(1);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {canManageConfig && (
                                        <button
                                            type="button"
                                            className="btn btn-dim btn-outline-primary"
                                            disabled={seedingTemplates}
                                            onClick={() =>
                                                seedTemplates({ data: {} })
                                            }
                                        >
                                            <em className="icon ni ni-reload-alt me-1"></em>
                                            {seedingTemplates
                                                ? "Syncing..."
                                                : "Sync templates"}
                                        </button>
                                    )}
                                </div>
                                {(templates || []).length === 0 &&
                                    !templatesQuery.isFetching && (
                                        <div className="alert alert-warning">
                                            No notification templates are
                                            configured yet. Use{" "}
                                            <strong>Sync templates</strong> to
                                            load the standard GEV process
                                            templates and channel links.
                                        </div>
                                    )}
                                <div className="row g-gs">
                                    <div className="col-lg-7">
                                        <div className="table-responsive">
                                            <table className="table table-tranx">
                                                <thead>
                                                    <tr className="tb-tnx-head">
                                                        <th>Template</th>
                                                        <th>Event</th>
                                                        <th>Enabled</th>
                                                        <th>Channels</th>
                                                        <th>Edit</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {templates.map(
                                                        (template) => (
                                                            <tr
                                                                key={template.id}
                                                            >
                                                                <td>
                                                                    <strong>
                                                                        {formatValue(
                                                                            template.template_name
                                                                        )}
                                                                    </strong>
                                                                    <div className="sub-text">
                                                                        {
                                                                            template.template_id
                                                                        }
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    {formatValue(
                                                                        template.app_service_id
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {template.is_notification_enabled ? (
                                                                        <span className="badge badge-dim bg-success">
                                                                            Yes
                                                                        </span>
                                                                    ) : (
                                                                        <span className="badge badge-dim bg-light text-dark">
                                                                            No
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {(
                                                                        template.dispensers ||
                                                                        []
                                                                    ).length}
                                                                </td>
                                                                <td>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        onClick={() =>
                                                                            setSelectedTemplate(
                                                                                {
                                                                                    ...template,
                                                                                    dispensers:
                                                                                        (
                                                                                            template.dispensers ||
                                                                                            []
                                                                                        ).map(
                                                                                            (
                                                                                                item
                                                                                            ) => ({
                                                                                                ...item,
                                                                                            })
                                                                                        ),
                                                                                }
                                                                            )
                                                                        }
                                                                    >
                                                                        Manage
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        )
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        <PaginationControls
                                            page={templatePage}
                                            pageCount={getNumPages(
                                                templatesQuery.data
                                            )}
                                            onPageChange={setTemplatePage}
                                        />
                                    </div>
                                    <div className="col-lg-5">
                                        {selectedTemplate ? (
                                            <div className="card card-bordered">
                                                <div className="card-inner">
                                                    <h6 className="title">
                                                        Edit Template
                                                    </h6>
                                                    <div className="mb-2">
                                                        <label className="form-label">
                                                            Template Name
                                                        </label>
                                                        <input
                                                            className="form-control"
                                                            value={
                                                                selectedTemplate.template_name ||
                                                                ""
                                                            }
                                                            onChange={(event) =>
                                                                setSelectedTemplate(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        template_name:
                                                                            event
                                                                                .target
                                                                                .value,
                                                                    })
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div className="mb-2">
                                                        <label className="form-label">
                                                            Subject
                                                        </label>
                                                        <input
                                                            className="form-control"
                                                            value={
                                                                selectedTemplate.subject ||
                                                                ""
                                                            }
                                                            onChange={(event) =>
                                                                setSelectedTemplate(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        subject:
                                                                            event
                                                                                .target
                                                                                .value,
                                                                    })
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div className="mb-2">
                                                        <label className="form-label">
                                                            Mobile Header
                                                        </label>
                                                        <input
                                                            className="form-control"
                                                            value={
                                                                selectedTemplate.header_mobile ||
                                                                ""
                                                            }
                                                            onChange={(event) =>
                                                                setSelectedTemplate(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        header_mobile:
                                                                            event
                                                                                .target
                                                                                .value,
                                                                    })
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div className="mb-2">
                                                        <label className="form-label">
                                                            Message
                                                        </label>
                                                        <textarea
                                                            className="form-control"
                                                            rows={4}
                                                            value={
                                                                selectedTemplate.message ||
                                                                ""
                                                            }
                                                            onChange={(event) =>
                                                                setSelectedTemplate(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        message:
                                                                            event
                                                                                .target
                                                                                .value,
                                                                    })
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div className="form-check mb-2">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={
                                                                !!selectedTemplate.is_allowed
                                                            }
                                                            onChange={(event) =>
                                                                setSelectedTemplate(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        is_allowed:
                                                                            event
                                                                                .target
                                                                                .checked,
                                                                    })
                                                                )
                                                            }
                                                        />
                                                        <label className="form-check-label">
                                                            Template allowed
                                                        </label>
                                                    </div>
                                                    <div className="form-check mb-3">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={
                                                                !!selectedTemplate.is_notification_enabled
                                                            }
                                                            onChange={(event) =>
                                                                setSelectedTemplate(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        is_notification_enabled:
                                                                            event
                                                                                .target
                                                                                .checked,
                                                                    })
                                                                )
                                                            }
                                                        />
                                                        <label className="form-check-label">
                                                            Notification enabled
                                                        </label>
                                                    </div>
                                                    <div className="mb-3">
                                                        <span className="preview-title overline-title">
                                                            Channel Links
                                                        </span>
                                                        {(selectedTemplate.dispensers ||
                                                            []).map((link) => (
                                                            <div
                                                                key={
                                                                    link.link_id
                                                                }
                                                                className="form-check"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    checked={
                                                                        !!link.is_allowed
                                                                    }
                                                                    onChange={(
                                                                        event
                                                                    ) =>
                                                                        setSelectedTemplate(
                                                                            (
                                                                                prev
                                                                            ) => ({
                                                                                ...prev,
                                                                                dispensers:
                                                                                    prev.dispensers.map(
                                                                                        (
                                                                                            item
                                                                                        ) =>
                                                                                            item.link_id ===
                                                                                            link.link_id
                                                                                                ? {
                                                                                                      ...item,
                                                                                                      is_allowed:
                                                                                                          event
                                                                                                              .target
                                                                                                              .checked,
                                                                                                  }
                                                                                                : item
                                                                                    ),
                                                                            })
                                                                        )
                                                                    }
                                                                />
                                                                <label className="form-check-label">
                                                                    {link
                                                                        .dispenser
                                                                        ?.dispenser_type ||
                                                                        "channel"}{" "}
                                                                    /{" "}
                                                                    {link
                                                                        .dispenser
                                                                        ?.service_provider ||
                                                                        "-"}
                                                                </label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary"
                                                        disabled={
                                                            updatingTemplate
                                                        }
                                                        onClick={
                                                            handleSaveTemplate
                                                        }
                                                    >
                                                        {updatingTemplate
                                                            ? "Saving..."
                                                            : "Save Template"}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="alert alert-light mb-0">
                                                Select a template to manage its
                                                content and channels.
                                            </div>
                                        )}
                                        <div className="card card-bordered mt-3">
                                            <div className="card-inner">
                                                <h6 className="title">
                                                    Active Dispensers
                                                </h6>
                                                {(dispensers || []).map(
                                                    (dispenser) => (
                                                        <div
                                                            key={dispenser.id}
                                                            className="d-flex justify-content-between py-1"
                                                        >
                                                            <span>
                                                                {dispenser.dispenser_type}{" "}
                                                                /{" "}
                                                                {
                                                                    dispenser.service_provider
                                                                }
                                                            </span>
                                                            <span className="sub-text">
                                                                {
                                                                    dispenser.linked_template_count
                                                                }{" "}
                                                                templates
                                                            </span>
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === "recipients" && (
                            <div className="card-inner">
                                <div className="alert alert-light mb-3">
                                    Configure which <strong>roles</strong> receive
                                    each process notification. Use{" "}
                                    <strong>Department</strong> scope when only
                                    users in the event&apos;s department should
                                    be notified (for example, department managers
                                    on requisition issued or QC results).
                                </div>
                                <div className="row g-3 mb-3">
                                    <div className="col-md-4">
                                        <input
                                            className="form-control"
                                            placeholder="Search event or template"
                                            value={recipientSearch}
                                            onChange={(event) =>
                                                setRecipientSearch(
                                                    event.target.value
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="row g-gs">
                                    <div className="col-lg-7">
                                        <div className="table-responsive">
                                            <table className="table table-tranx">
                                                <thead>
                                                    <tr className="tb-tnx-head">
                                                        <th>Event</th>
                                                        <th>Roles</th>
                                                        <th>Scope</th>
                                                        <th>Edit</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {recipientTemplates.map(
                                                        (template) => (
                                                            <tr
                                                                key={template.id}
                                                            >
                                                                <td>
                                                                    <strong>
                                                                        {formatValue(
                                                                            template.template_name
                                                                        )}
                                                                    </strong>
                                                                    <div className="sub-text">
                                                                        {formatValue(
                                                                            template.app_service_id
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    {(
                                                                        template
                                                                            .recipient_summary
                                                                            ?.roles ||
                                                                        []
                                                                    ).map(
                                                                        (
                                                                            role
                                                                        ) => (
                                                                            <span
                                                                                key={`${template.id}-${role}`}
                                                                                className="badge badge-dim bg-primary me-1 mb-1"
                                                                            >
                                                                                {
                                                                                    role
                                                                                }
                                                                            </span>
                                                                        )
                                                                    )}
                                                                    {!(
                                                                        template
                                                                            .recipient_summary
                                                                            ?.roles ||
                                                                        []
                                                                    ).length && (
                                                                        <span className="sub-text">
                                                                            No
                                                                            rules
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {template
                                                                        .recipient_summary
                                                                        ?.uses_department_scope ? (
                                                                        <ScopeBadge scope="department" />
                                                                    ) : (
                                                                        <ScopeBadge scope="global" />
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        onClick={() =>
                                                                            setSelectedRecipientTemplate(
                                                                                {
                                                                                    ...template,
                                                                                    recipient_rules:
                                                                                        (
                                                                                            template.recipient_rules ||
                                                                                            []
                                                                                        ).map(
                                                                                            (
                                                                                                rule
                                                                                            ) => ({
                                                                                                ...rule,
                                                                                            })
                                                                                        ),
                                                                                }
                                                                            )
                                                                        }
                                                                    >
                                                                        Configure
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        )
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    <div className="col-lg-5">
                                        {selectedRecipientTemplate ? (
                                            <div className="card card-bordered">
                                                <div className="card-inner">
                                                    <h6 className="title">
                                                        Recipient Rules
                                                    </h6>
                                                    <p className="sub-text mb-3">
                                                        {formatValue(
                                                            selectedRecipientTemplate.template_name
                                                        )}{" "}
                                                        (
                                                        {formatValue(
                                                            selectedRecipientTemplate.app_service_id
                                                        )}
                                                        )
                                                    </p>
                                                    {(selectedRecipientTemplate.recipient_rules ||
                                                        []).map(
                                                        (rule, index) => (
                                                            <div
                                                                key={`${rule.role_value}-${rule.scope}-${index}`}
                                                                className="border rounded p-2 mb-2"
                                                            >
                                                                <div className="mb-2">
                                                                    <label className="form-label">
                                                                        Role
                                                                    </label>
                                                                    <select
                                                                        className="form-select form-select-sm"
                                                                        value={
                                                                            rule.role_value ||
                                                                            ""
                                                                        }
                                                                        onChange={(
                                                                            event
                                                                        ) =>
                                                                            setSelectedRecipientTemplate(
                                                                                (
                                                                                    prev
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    recipient_rules:
                                                                                        prev.recipient_rules.map(
                                                                                            (
                                                                                                item,
                                                                                                ruleIndex
                                                                                            ) =>
                                                                                                ruleIndex ===
                                                                                                index
                                                                                                    ? {
                                                                                                          ...item,
                                                                                                          role_value:
                                                                                                              event
                                                                                                                  .target
                                                                                                                  .value,
                                                                                                      }
                                                                                                    : item
                                                                                        ),
                                                                                })
                                                                            )
                                                                        }
                                                                    >
                                                                        {availableRoles.map(
                                                                            (
                                                                                role
                                                                            ) => (
                                                                                <option
                                                                                    key={
                                                                                        role.id
                                                                                    }
                                                                                    value={
                                                                                        role.role_value
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        role.role_name
                                                                                    }{" "}
                                                                                    (
                                                                                    {
                                                                                        role.role_value
                                                                                    }
                                                                                    )
                                                                                </option>
                                                                            )
                                                                        )}
                                                                    </select>
                                                                </div>
                                                                <div className="mb-2">
                                                                    <label className="form-label">
                                                                        Scope
                                                                    </label>
                                                                    <select
                                                                        className="form-select form-select-sm"
                                                                        value={
                                                                            rule.scope ||
                                                                            "global"
                                                                        }
                                                                        onChange={(
                                                                            event
                                                                        ) =>
                                                                            setSelectedRecipientTemplate(
                                                                                (
                                                                                    prev
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    recipient_rules:
                                                                                        prev.recipient_rules.map(
                                                                                            (
                                                                                                item,
                                                                                                ruleIndex
                                                                                            ) =>
                                                                                                ruleIndex ===
                                                                                                index
                                                                                                    ? {
                                                                                                          ...item,
                                                                                                          scope: event
                                                                                                              .target
                                                                                                              .value,
                                                                                                      }
                                                                                                    : item
                                                                                        ),
                                                                                })
                                                                            )
                                                                        }
                                                                    >
                                                                        {scopeOptions.map(
                                                                            (
                                                                                option
                                                                            ) => (
                                                                                <option
                                                                                    key={
                                                                                        option.value
                                                                                    }
                                                                                    value={
                                                                                        option.value
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        option.label
                                                                                    }
                                                                                </option>
                                                                            )
                                                                        )}
                                                                    </select>
                                                                </div>
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <div className="form-check">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="form-check-input"
                                                                            checked={
                                                                                rule.is_enabled !==
                                                                                false
                                                                            }
                                                                            onChange={(
                                                                                event
                                                                            ) =>
                                                                                setSelectedRecipientTemplate(
                                                                                    (
                                                                                        prev
                                                                                    ) => ({
                                                                                        ...prev,
                                                                                        recipient_rules:
                                                                                            prev.recipient_rules.map(
                                                                                                (
                                                                                                    item,
                                                                                                    ruleIndex
                                                                                                ) =>
                                                                                                    ruleIndex ===
                                                                                                    index
                                                                                                        ? {
                                                                                                              ...item,
                                                                                                              is_enabled:
                                                                                                                  event
                                                                                                                      .target
                                                                                                                      .checked,
                                                                                                          }
                                                                                                        : item
                                                                                            ),
                                                                                    })
                                                                                )
                                                                            }
                                                                        />
                                                                        <label className="form-check-label">
                                                                            Enabled
                                                                        </label>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-danger"
                                                                        onClick={() =>
                                                                            handleRemoveRecipientRule(
                                                                                index
                                                                            )
                                                                        }
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-light mb-3"
                                                        onClick={
                                                            handleAddRecipientRule
                                                        }
                                                        disabled={
                                                            !availableRoles.length
                                                        }
                                                    >
                                                        Add role rule
                                                    </button>
                                                    <div>
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary"
                                                            disabled={
                                                                updatingTemplate
                                                            }
                                                            onClick={
                                                                handleSaveRecipientRules
                                                            }
                                                        >
                                                            {updatingTemplate
                                                                ? "Saving..."
                                                                : "Save Recipient Rules"}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="alert alert-light mb-0">
                                                Select an event to configure role
                                                and department-based recipients.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div
                className="modal fade m-0 p-0"
                id="notification-log-detail-modal"
                tabIndex={-1}
                aria-modal="true"
                role="dialog"
            >
                <div className="modal-dialog modal-xl" role="document">
                    <div className="modal-content">
                        <button
                            type="button"
                            className="close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                        >
                            <em className="icon ni ni-cross"></em>
                        </button>
                        <div className="modal-header">
                            <h5 className="modal-title">
                                Notification Details
                            </h5>
                        </div>
                        <div className="modal-body">
                            {selectedLog ? (
                                <>
                                    <p>
                                        <strong>Date:</strong>{" "}
                                        {formatDate(selectedLog.occurred_at)}
                                    </p>
                                    <p>
                                        <strong>Event:</strong>{" "}
                                        {formatValue(selectedLog.event_key)}
                                    </p>
                                    <p>
                                        <strong>Recipient:</strong>{" "}
                                        {formatValue(
                                            selectedLog.recipient?.username
                                        )}{" "}
                                        (
                                        {formatValue(
                                            selectedLog.recipient?.email
                                        )}
                                        )
                                    </p>
                                    <p>
                                        <strong>Subject:</strong>{" "}
                                        {formatValue(selectedLog.final_subject)}
                                    </p>
                                    <pre className="bg-lighter p-2 rounded small overflow-auto">
                                        {selectedLog.final_message}
                                    </pre>
                                    <h6 className="title mt-3">
                                        Delivery Attempts
                                    </h6>
                                    {(selectedLog.delivery_statuses || []).map(
                                        (row) => (
                                            <div
                                                key={row.id}
                                                className="border rounded p-2 mb-2"
                                            >
                                                <div>
                                                    {row.dispenser
                                                        ?.dispenser_type ||
                                                        "channel"}{" "}
                                                    /{" "}
                                                    {row.dispenser
                                                        ?.service_provider ||
                                                        "-"}
                                                </div>
                                                <div>
                                                    Status:{" "}
                                                    {formatValue(
                                                        row.delivery_status
                                                    )}
                                                </div>
                                                <div className="sub-text">
                                                    {formatValue(
                                                        row.error_comments
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    )}
                                </>
                            ) : (
                                <div className="text-soft">
                                    No notification selected.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default NotificationConsole;
